import { useCallback, useEffect, useState } from 'react';

import { useUserMutations, useStorage } from '.';
import { AlertBarDataType, DialogDataType } from '../components';
import { DEFAULT_USER_DATA } from '../config';
import { UserAction, getUserDialogTitle } from '../enums';
import {
  UpdateUserMutationInputType,
  UserActionHandlerInputType,
  UserType,
  UserDataInputType,
} from '../types';

type Props = {
  data?: UserType;
  isDataFetching: boolean;
  setDialogData: (variables: DialogDataType | null) => void;
  setSnackbarData: (variables: AlertBarDataType | null) => void;
};

type ReturnType = {
  data: {
    isLoading: boolean;
    userData?: UserType;
  };
  dispatchAction: ({ action }: UserActionHandlerInputType) => void;
};

export const useUserData = ({
  isDataFetching,
  setDialogData,
  setSnackbarData,
}: Props): ReturnType => {
  const [userData, setUserData] = useState<UserType>();

  const {
    errorMessage: storageErrorMessage,
    getImageUrl,
    isLoading: isStorageLoading,
    removeFile,
    uploadFile,
  } = useStorage();

  const {
    updateUser,
    errorMessage: userMutationErrorMessage,
    hasError: userMutationHasError,
    isLoading: isUserMutationLoading,
  } = useUserMutations();

  const handleDefaultUserData = useCallback(
    ({ data }: { data?: UserType } = {}) => ({
      id: data?.id || DEFAULT_USER_DATA.id,
      firstName: data?.firstName || DEFAULT_USER_DATA.firstName,
      lastName: data?.lastName || DEFAULT_USER_DATA.lastName,
      biography: data?.biography || DEFAULT_USER_DATA.biography,
      location: data?.location || DEFAULT_USER_DATA.location,
      image: data?.image
        ? getImageUrl({ fileName: data?.image || '' })
        : DEFAULT_USER_DATA.image,
      intro: data?.intro || DEFAULT_USER_DATA.intro,
      about: data?.about || DEFAULT_USER_DATA.about,
      experience: data?.experience || DEFAULT_USER_DATA.experience,
    }),
    [getImageUrl],
  );

  const onUpdateUser = useCallback(
    async ({ input }: { input: UpdateUserMutationInputType }) => {
      const response = await updateUser(input);
      const { user } = response?.data || {};
      if (user) {
        setSnackbarData({
          severity: 'success',
          text: 'Profile updated successfully',
        });
        const updatedUser = handleDefaultUserData({ data: user });
        setUserData(updatedUser);
      } else {
        setSnackbarData({
          severity: 'error',
          text: 'Error updating profile',
        });
      }
      return response;
    },
    [handleDefaultUserData, setSnackbarData, updateUser],
  );

  const onUploadUserImage = useCallback(
    async ({ file, ...user }: UserDataInputType) => {
      if (file) {
        const response = await uploadFile({ file });
        if (response?.success) {
          user.image = response.fileName;
          onUpdateUser({ input: { user } });
        } else {
          setSnackbarData({
            severity: 'error',
            text: 'Error updating image',
          });
        }
        return response;
      }
    },
    [onUpdateUser, setSnackbarData, uploadFile],
  );

  const onRemoveUserImage = useCallback(
    async ({ ...user }: UserDataInputType) => {
      if (user?.image) {
        const response = await removeFile({ fileName: user?.image });
        if (response?.success) {
          user.image = '';
          await onUpdateUser({ input: { user } });
        } else {
          setSnackbarData({
            severity: 'error',
            text: 'Error removing image',
          });
        }
        return response;
      }
    },
    [onUpdateUser, removeFile, setSnackbarData],
  );

  const dispatchAction = useCallback(
    ({ action, data, shouldConfirm = false }: UserActionHandlerInputType) => {
      const executeAction = () => {
        switch (action) {
          case UserAction.UPDATE:
            if (data) {
              onUpdateUser({ input: { user: data } });
            }
            return;
          case UserAction.UPLOAD_IMAGE:
            if (data) {
              onUploadUserImage({ ...data });
            }
            return;
          case UserAction.REMOVE_IMAGE:
            if (data) {
              onRemoveUserImage({ ...data });
            }
            return;
          default: {
            const exhaustiveCase: never = action;
            throw new Error(`Unhandled UserAction case: ${exhaustiveCase}`);
          }
        }
      };

      if (shouldConfirm) {
        setDialogData({
          dialogTitle: getUserDialogTitle({ action }),
          isDialogOpen: true,
          onCloseDialog: () => setDialogData(null),
          onConfirmDialog: () => {
            executeAction();
            setDialogData(null);
          },
        });
      } else {
        executeAction();
      }
    },
    [onRemoveUserImage, onUpdateUser, onUploadUserImage, setDialogData],
  );

  useEffect(() => {
    if (!isDataFetching) {
      const user = handleDefaultUserData();
      setUserData(user);
    }
  }, [handleDefaultUserData, isDataFetching]);

  useEffect(() => {
    if (userMutationHasError) {
      setSnackbarData({
        severity: 'error',
        text:
          userMutationErrorMessage ??
          `An error occured: ${userMutationErrorMessage}`,
      });
    }
  }, [userMutationHasError, userMutationErrorMessage, setSnackbarData]);

  useEffect(() => {
    if (storageErrorMessage) {
      setSnackbarData({
        severity: 'error',
        text: storageErrorMessage ?? `An error occured: ${storageErrorMessage}`,
      });
    }
  }, [storageErrorMessage, setSnackbarData]);

  return {
    data: {
      isLoading: isUserMutationLoading || isStorageLoading || isDataFetching,
      userData,
    },
    dispatchAction,
  };
};
